import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardTitle, CardText, Badge, Button, Collapse, Alert } from 'reactstrap';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import instance from '../../api/Axios';

const GeneralDashboard = () => {
  const { userId, userEmail, userType, isAuthenticated } = useAuth();
  const { 
    permissions, 
    loading: permissionsLoading, 
    refreshPermissions 
  } = usePermissions();
  const [showDebug, setShowDebug] = useState(false);
  const [permissionTemplates, setPermissionTemplates] = useState([]);

  // Create a user object for compatibility
  const user = {
    id: userId,
    email: userEmail,
    user_type: userType
  };

  console.log('GeneralDashboard rendered, user:', user);
  console.log('Permissions from hook:', permissions);
  console.log('Permissions loading:', permissionsLoading);

  useEffect(() => {
    const fetchPermissionTemplates = async () => {
      if (!userId) return;
      
      try {
        const response = await instance.get('/api/accounts/permissions/user_permissions/');
        setPermissionTemplates(response.data.permission_templates || []);
      } catch (error) {
        console.error('Error fetching permission templates:', error);
      }
    };

    fetchPermissionTemplates();
  }, [userId]);

  const formatUserType = (userType) => {
    return userType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getUserTypeColor = (userType) => {
    const colors = {
      'student': 'primary',
      'accommodation_provider': 'success',
      'primary_representative': 'info',
      'secondary_representative': 'info',
      'bmo_officer': 'warning',
      'funder_primary_representative': 'secondary',
      'funder_secondary_representative': 'secondary',
      'funder_officer': 'secondary'
    };
    return colors[userType] || 'light';
  };

  return (
    <Container fluid className="p-4">
      {/* Debug Section */}
      <Row className="mb-4">
        <Col>
          <Card className="border-warning">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <CardTitle tag="h6" className="text-warning mb-0">
                    <i className="bi bi-bug me-2"></i>
                    Debug: User Permissions
                  </CardTitle>
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    color="primary" 
                    size="sm"
                    onClick={refreshPermissions}
                    disabled={permissionsLoading}
                  >
                    {permissionsLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                  <Button 
                    color="outline-warning" 
                    size="sm"
                    onClick={() => setShowDebug(!showDebug)}
                  >
                    {showDebug ? 'Hide' : 'Show'} Permissions
                  </Button>
                </div>
              </div>
              
              <Collapse isOpen={showDebug}>
                <hr />
                {permissionsLoading ? (
                  <div className="text-center">
                    <i className="bi bi-hourglass-split me-2"></i>
                    Loading permissions...
                  </div>
                ) : (
                  <Row>
                    <Col md={6}>
                      <h6 className="text-primary">Individual Permissions ({permissions?.length || 0})</h6>
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {permissions && permissions.length > 0 ? (
                          permissions.map((perm, index) => (
                            <div key={index} className="mb-2">
                              <Badge color="primary" className="me-2">
                                {perm.content_type?.app_label}.{perm.codename}
                              </Badge>
                              <small className="text-muted">{perm.name}</small>
                            </div>
                          ))
                        ) : (
                          <Alert color="info" className="py-2">
                            No individual permissions found
                          </Alert>
                        )}
                      </div>
                    </Col>
                    
                    <Col md={6}>
                      <h6 className="text-success">Permission Templates/Groups ({permissionTemplates?.length || 0})</h6>
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {permissionTemplates && permissionTemplates.length > 0 ? (
                          permissionTemplates.map((template, index) => (
                            <div key={index} className="mb-2">
                              <Badge color="success" className="me-2">
                                {template.name}
                              </Badge>
                              <small className="text-muted">
                                {template.description || 'No description'}
                              </small>
                              {template.permission_count && (
                                <div className="ms-3 mt-1">
                                  <small className="text-muted">
                                    {template.permission_count} permissions in this group
                                  </small>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <Alert color="info" className="py-2">
                            No permission templates assigned
                          </Alert>
                        )}
                      </div>
                    </Col>
                  </Row>
                )}
                
                <hr />
                <Row>
                  <Col>
                    <h6 className="text-info">User Information</h6>
                    <div className="d-flex flex-wrap gap-2">
                      <Badge color="info">Type: {user?.user_type}</Badge>
                      <Badge color="info">Email: {user?.email}</Badge>
                      <Badge color="info">ID: {user?.id}</Badge>
                      {user?.is_staff && <Badge color="warning">Staff</Badge>}
                      {user?.is_superuser && <Badge color="danger">Superuser</Badge>}
                    </div>
                  </Col>
                </Row>
              </Collapse>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">Welcome, {user?.first_name || 'User'}!</h2>
              <p className="text-muted mb-0">
                <Badge color={getUserTypeColor(user?.user_type)} className="me-2">
                  {formatUserType(user?.user_type)}
                </Badge>
                Manage your account and access your services
              </p>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={8}>
          <Row className="g-3">
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <CardTitle tag="h5" className="text-primary">
                    <i className="bi bi-person-circle me-2"></i>
                    Account Information
                  </CardTitle>
                  <CardText>
                    <strong>Name:</strong> {user?.first_name} {user?.last_name}<br/>
                    <strong>Email:</strong> {user?.email}<br/>
                    <strong>Account Type:</strong> {formatUserType(user?.user_type)}
                  </CardText>
                </CardBody>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <CardTitle tag="h5" className="text-success">
                    <i className="bi bi-file-earmark-text me-2"></i>
                    Quick Actions
                  </CardTitle>
                  <div className="d-grid gap-2">
                    <Button color="outline-primary" size="sm">
                      <i className="bi bi-person-gear me-2"></i>
                      Update Profile
                    </Button>
                    <Button color="outline-secondary" size="sm">
                      <i className="bi bi-file-arrow-up me-2"></i>
                      Upload Documents
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <CardTitle tag="h5" className="text-info">
                    <i className="bi bi-bell me-2"></i>
                    Recent Activity
                  </CardTitle>
                  <CardText className="text-muted">
                    No recent activity to display.
                  </CardText>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <CardBody>
              <CardTitle tag="h5" className="text-warning">
                <i className="bi bi-info-circle me-2"></i>
                Getting Started
              </CardTitle>
              <CardText>
                Welcome to your dashboard! Here you can:
              </CardText>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  View your account information
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  Manage your profile settings
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  Upload and manage documents
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle text-success me-2"></i>
                  Access notifications
                </li>
              </ul>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default GeneralDashboard;
